import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOnboarding } from '../contexts/OnboardingContext';
import { Button } from './ui/button';
import { Target, Sparkles, Play, X } from 'lucide-react';

const WelcomeModal = () => {
  const location = useLocation();
  const { hasCompletedOnboarding, startOnboarding, isOnboardingActive } = useOnboarding();
  const [showWelcome, setShowWelcome] = useState(false);

  // Routes where onboarding should NOT show
  const publicRoutes = ['/', '/login', '/payment-success', '/payment-cancelled'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  useEffect(() => {
    // Only show welcome modal on protected routes (after login)
    if (isPublicRoute) {
      setShowWelcome(false);
      return;
    }

    // Show welcome modal for new users after a short delay
    const hasSeenWelcome = localStorage.getItem('alvo_seen_welcome');
    
    if (!hasSeenWelcome && !hasCompletedOnboarding && !isOnboardingActive) {
      const timer = setTimeout(() => {
        setShowWelcome(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasCompletedOnboarding, isOnboardingActive, isPublicRoute]);

  const handleStartTour = () => {
    localStorage.setItem('alvo_seen_welcome', 'true');
    setShowWelcome(false);
    startOnboarding();
  };

  const handleSkip = () => {
    localStorage.setItem('alvo_seen_welcome', 'true');
    localStorage.setItem('alvo_onboarding_completed', 'true');
    setShowWelcome(false);
  };

  if (!showWelcome) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gradient-to-b from-zinc-900 to-zinc-950 border border-zinc-700/50 rounded-3xl p-8 shadow-2xl animate-in fade-in zoom-in duration-500">
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors p-2"
        >
          <X size={20} />
        </button>

        {/* Logo/Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-2xl animate-pulse" />
            <div className="relative p-5 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-full border border-emerald-500/30">
              <Target className="w-12 h-12 text-emerald-400" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Bem-vindo ao <span className="text-emerald-400">Alvo</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Seu copiloto financeiro inteligente
          </p>
        </div>

        {/* Features preview */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          <div className="p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
            <div className="text-emerald-400 text-xl mb-1">📊</div>
            <p className="text-sm text-zinc-300">Controle financeiro completo</p>
          </div>
          <div className="p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
            <div className="text-emerald-400 text-xl mb-1">🤖</div>
            <p className="text-sm text-zinc-300">IA que analisa seu negócio</p>
          </div>
          <div className="p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
            <div className="text-emerald-400 text-xl mb-1">📈</div>
            <p className="text-sm text-zinc-300">Projeções inteligentes</p>
          </div>
          <div className="p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
            <div className="text-emerald-400 text-xl mb-1">💡</div>
            <p className="text-sm text-zinc-300">Insights automáticos</p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={handleStartTour}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base rounded-xl"
            data-testid="start-tour-button"
          >
            <Play size={20} className="mr-2" />
            Fazer Tour Guiado
            <Sparkles size={16} className="ml-2 text-emerald-300" />
          </Button>
          
          <button
            onClick={handleSkip}
            className="w-full py-3 text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
            data-testid="skip-tour-button"
          >
            Já conheço, pular introdução
          </button>
        </div>

        {/* Tip */}
        <p className="mt-6 text-xs text-zinc-600 text-center">
          💡 Dica: Você pode reiniciar o tour a qualquer momento em Configurações
        </p>
      </div>
    </div>
  );
};

export default WelcomeModal;
